import type { ReactNode } from "react";
import type { SourcingEventDto } from "../../../autogen/bff-api";
import { allowsPartIIDialogue } from "../rounds/partIIDialogue";
import { useTranslation } from "react-i18next";
import { NotReadyCard } from "../NotReadyCard";
import { unscoredRoundTenders } from "../evaluation/evaluationCompleteness";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { useAwardSelection } from "./AwardSelectionContext";

export const RoundTendersScoredGate = ({ event, children }: { event: SourcingEventDto; children: ReactNode }) => {
  const { t } = useTranslation();
  const { rounds } = useRoundSelection();
  const { evaluation } = useAwardSelection();
  const restored = evaluation?.restoredTendersNotReady ?? [];
  if (restored.length > 0) {
    const leftOut = restored.filter((tender) => tender.leftOutOfRound).map((tender) => tender.supplierName);
    const notEvaluated = restored.filter((tender) => !tender.leftOutOfRound).map((tender) => tender.supplierName);
    const reasons = [
      notEvaluated.length > 0
        ? t("The rejection of {{suppliers}} has been undone. Qualify and evaluate the tender before you award.", {
            suppliers: notEvaluated.join(", "),
          })
        : null,
      leftOut.length > 0
        ? t(
            "The rejection of {{suppliers}} has been undone, but the supplier is not invited to the latest negotiation round. Invite the supplier to a round before you award.",
            { suppliers: leftOut.join(", ") },
          )
        : null,
    ];
    return (
      <NotReadyCard
        title={String(t("The award is not ready yet"))}
        description={reasons.filter(Boolean).map(String).join(" ")}
      />
    );
  }
  const unscored = unscoredRoundTenders(evaluation, rounds);
  if (unscored.length === 0) return <>{children}</>;
  return (
    <NotReadyCard
      title={String(t("The award is not ready yet"))}
      description={String(
        allowsPartIIDialogue(event)
          ? t("Score {{suppliers}} before you award. Tenderers that delivered nothing in the round stay in on their latest tender.", {
              suppliers: unscored.map((bid) => bid.supplierName).join(", "),
            })
          : t("Score {{suppliers}} in the latest round before you award. Only the tenders from the latest round count.", {
              suppliers: unscored.map((bid) => bid.supplierName).join(", "),
            }),
      )}
    />
  );
};
